import { Component, h, Prop, State, Element, Listen } from '@stencil/core';

@Component({
  tag: 'vs-card-editorial-grid',
  styleUrl: 'vs-card-editorial-grid.scss',
  shadow: false,
})
export class VsCardEditorialGrid {
  @Element() el!: HTMLElement;

  @Prop() heroEyebrow: string;
  @Prop() heroTitle: string;
  @Prop() heroDescription: string;
  @Prop() heroImageSrc: string;
  @Prop() heroMediaType: string;
  @Prop() heroBackgroundVideo: string;
  @Prop() heroMobileBackgroundVideo: string;
  @Prop() cards: any;
  @Prop() titleBlock: string;
  @Prop() heroCtaData: any;

  @State() isMobile: boolean = false;
  @State() isTablet: boolean = false;
  @State() currentSlideIndex: number = 0;
  @State() canScrollLeft: boolean = false;
  @State() canScrollRight: boolean = true;
  @State() isScrolling: boolean = false;

  cardGridRef: HTMLDivElement;
  gridSectionRef: HTMLDivElement;
  carouselRef: HTMLDivElement;

  componentWillLoad() {
    // Parse cards if it's a string
    if (typeof this.cards === 'string') {
      try {
        this.cards = JSON.parse(this.cards);
      } catch (e) {
        this.cards = [];
        console.warn('Invalid cards prop passed to vs-card-editorial-grid', e);
      }
    }

    // Parse heroCtaData if it's a string
    if (typeof this.heroCtaData === 'string') {
      try {
        this.heroCtaData = JSON.parse(this.heroCtaData);
      } catch (e) {
        this.heroCtaData = [];
        console.warn('Invalid heroCtaData prop passed to vs-card-editorial-grid', e);
      }
    }

    // Check initial screen size with correct breakpoints
    this.checkScreenSize();
  }

  componentDidLoad() {
    // Add resize event listener
    window.addEventListener('resize', this.checkScreenSize.bind(this));

    // Find the editorial-card-grid element
    window.setTimeout(() => {
      this.cardGridRef = this.el.querySelector('.editorial-card-grid');
      this.carouselRef = this.el.querySelector('.scrollable-cards');
      if (this.carouselRef) {
        this.updateScrollState();
        this.carouselRef.addEventListener('scroll', this.updateScrollState.bind(this));

        // Also update on initial load to ensure proper button states
        setTimeout(() => this.updateScrollState(), 100);
      }
    }, 0);
  }

  disconnectedCallback() {
    // Remove resize event listener when component is removed
    window.removeEventListener('resize', this.checkScreenSize.bind(this));

    // Remove scroll event listener
    if (this.carouselRef) {
      this.carouselRef.removeEventListener('scroll', this.updateScrollState.bind(this));
    }
  }

  @Listen('resize', { target: 'window' })
  checkScreenSize() {
    const width = window.innerWidth;
    this.isMobile = width <= 767; // Mobile: ≤ 767px
    this.isTablet = width >= 768 && width <= 991; // Tablet: 768-991px

    // Update scroll state on resize for mobile and tablet
    if ((this.isMobile || this.isTablet) && this.carouselRef) {
      setTimeout(() => this.updateScrollState(), 100);
    }
  }

  updateScrollState() {
    if (!this.carouselRef) return;

    const scrollLeft = this.carouselRef.scrollLeft;
    const scrollWidth = this.carouselRef.scrollWidth;
    const clientWidth = this.carouselRef.clientWidth;
    const maxScrollLeft = scrollWidth - clientWidth;

    // Calculate how many cards are visible and total cards
    const totalCards = this.cards?.length || 0;
    let visibleCards = 1; // Default for mobile

    if (this.isTablet) {
      visibleCards = 2; // Show 2 cards on tablet
    }

    // Check if we have enough cards to warrant scrolling
    const hasEnoughCardsToScroll = totalCards > visibleCards;

    // Very strict boundary detection
    const tolerance = 3; // Reduced tolerance

    // Left boundary: can scroll left if we're not at the very beginning
    this.canScrollLeft = hasEnoughCardsToScroll && scrollLeft > tolerance;

    // Right boundary: can scroll right if we're not at the very end
    this.canScrollRight = hasEnoughCardsToScroll && scrollLeft < maxScrollLeft - tolerance;

    // Additional safety check: if maxScrollLeft is very small or zero, disable right scroll
    if (maxScrollLeft <= tolerance) {
      this.canScrollRight = false;
    }
  }

  scrollToNext() {
    if (!this.carouselRef || !this.canScrollRight || this.isScrolling) return;

    this.isScrolling = true;

    const scrollLeft = this.carouselRef.scrollLeft;
    const scrollWidth = this.carouselRef.scrollWidth;
    const clientWidth = this.carouselRef.clientWidth;
    const maxScrollLeft = scrollWidth - clientWidth;

    // If we're already at or very close to the end, don't scroll
    if (scrollLeft >= maxScrollLeft - 5) {
      this.isScrolling = false;
      return;
    }

    const cardWidth =
      this.carouselRef.querySelector('.mobile-card, .tablet-card')?.clientWidth || 0;
    const gap = this.isTablet ? 24 : 16; // Different gaps for tablet vs mobile
    let scrollAmount;

    if (this.isTablet) {
      // For tablet, scroll by 2 cards width (since we show 2 cards per frame)
      scrollAmount = cardWidth * 2 + gap * 2;
    } else {
      // For mobile, scroll by 1 card width
      scrollAmount = cardWidth + gap;
    }

    // Calculate new scroll position, but don't exceed the maximum
    const newScrollPosition = Math.min(scrollLeft + scrollAmount, maxScrollLeft);

    // Only scroll if there's meaningful movement and we're not at the end
    if (newScrollPosition > scrollLeft + 5 && newScrollPosition < maxScrollLeft + 5) {
      this.carouselRef.scrollTo({
        left: newScrollPosition,
        behavior: 'smooth',
      });
    } else {
      // If we can't scroll meaningfully, just go to the exact end
      this.carouselRef.scrollTo({
        left: maxScrollLeft,
        behavior: 'smooth',
      });
    }

    // Update scroll state after animation completes
    setTimeout(() => {
      this.updateScrollState();
      this.isScrolling = false;
    }, 400);
  }

  scrollToPrev() {
    if (!this.carouselRef || !this.canScrollLeft || this.isScrolling) return;

    this.isScrolling = true;

    const scrollLeft = this.carouselRef.scrollLeft;

    // If we're already at or very close to the beginning, don't scroll
    if (scrollLeft <= 5) {
      this.isScrolling = false;
      return;
    }

    const cardWidth =
      this.carouselRef.querySelector('.mobile-card, .tablet-card')?.clientWidth || 0;
    const gap = this.isTablet ? 24 : 16; // Different gaps for tablet vs mobile
    let scrollAmount;

    if (this.isTablet) {
      // For tablet, scroll by 2 cards width (since we show 2 cards per frame)
      scrollAmount = cardWidth * 2 + gap * 2;
    } else {
      // For mobile, scroll by 1 card width
      scrollAmount = cardWidth + gap;
    }

    // Calculate new scroll position, ensuring we don't go below 0
    const newScrollPosition = Math.max(scrollLeft - scrollAmount, 0);

    // Only scroll if there's meaningful movement
    if (newScrollPosition < scrollLeft - 5) {
      this.carouselRef.scrollTo({
        left: newScrollPosition,
        behavior: 'smooth',
      });
    } else {
      // If we can't scroll meaningfully, just go to the exact beginning
      this.carouselRef.scrollTo({
        left: 0,
        behavior: 'smooth',
      });
    }

    // Update scroll state after animation completes
    setTimeout(() => {
      this.updateScrollState();
      this.isScrolling = false;
    }, 400);
  }

  render() {
    return (
      <div
        class={`vs-card-editorial-grid ${this.isMobile ? 'mobile-view' : ''} ${this.isTablet ? 'tablet-view' : ''}`}
      >
        <vsr-title
          title-block={this.titleBlock}
          is-padding-horizontal="false"
          theme="light"
        ></vsr-title>

        <div
          class={`editorial-grid grid ${this.isMobile ? 'mobile-grid' : this.isTablet ? 'tablet-grid' : 'desktop-grid'} ${this.cards?.length > 4 ? 'cards-more-than-4' : 'cards-4-or-less'}`}
          ref={el => (this.gridSectionRef = el as HTMLDivElement)}
          data-animation-container
        >
          <div
            class={`editorial-hero-card ${this.isMobile ? 'mobile-layout' : this.isTablet ? 'tablet-layout' : ''}`}
          >
            <div class="image-container cardFlipReveal">
              {this.heroMediaType === 'video' ? (
                <div class="video-container">
                  <video
                    class="video"
                    autoplay
                    muted
                    loop
                    playsinline
                    poster={this.heroImageSrc}
                    src={
                      this.isMobile && this.heroMobileBackgroundVideo
                        ? this.heroMobileBackgroundVideo
                        : this.heroBackgroundVideo
                    }
                  ></video>
                </div>
              ) : (
                <div
                  class="image"
                  style={{
                    backgroundImage: this.heroImageSrc ? `url(${this.heroImageSrc})` : 'none',
                  }}
                ></div>
              )}
              {!this.isMobile && <div class="tint"></div>}
            </div>
            <div class="copy-container">
              {this.heroEyebrow && <span class="eyebrow">{this.heroEyebrow}</span>}
              <h3 class="title">{this.heroTitle}</h3>
              {this.heroDescription && <p class="description">{this.heroDescription}</p>}
              <a
                href={this.heroCtaData?.length ? this.heroCtaData[0].ctaLink || '#' : '#'}
                target={
                  this.heroCtaData?.length ? this.heroCtaData[0].ctaLinkTarget || '_self' : '_self'
                }
                class={
                  this.heroCtaData?.length && this.heroCtaData[0].ctaStyle === 'link'
                    ? 'analytics-button text-link'
                    : 'analytics-button button-link'
                }
              >
                <span>{this.heroCtaData?.length ? this.heroCtaData[0].ctaTitle : ''}</span>
                {this.heroCtaData?.length && this.heroCtaData[0].ctaStyle === 'link' && (
                  <svg
                    class="arrow-icon"
                    width="16"
                    height="16"
                    viewBox="0 0 16 16"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M8 0L6.59 1.41L12.17 7H0V9H12.17L6.59 14.59L8 16L16 8L8 0Z"
                      fill="currentColor"
                    />
                  </svg>
                )}
              </a>
            </div>
          </div>

          {this.isMobile || this.isTablet ? (
            <div class={`${this.isMobile ? 'mobile-carousel' : 'tablet-carousel'}`}>
              {this.canScrollLeft && (
                <button
                  class={`nav-button prev-button ${this.isScrolling ? 'disabled' : ''}`}
                  onClick={() => this.scrollToPrev()}
                  disabled={this.isScrolling}
                  aria-label="Previous cards"
                >
                  <svg
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path d="M15.41 7.41L14 6l-6 6 6 6 1.41-1.41L10.83 12z" fill="currentColor" />
                  </svg>
                </button>
              )}
              {this.canScrollRight && (
                <button
                  class={`nav-button next-button ${this.isScrolling ? 'disabled' : ''}`}
                  onClick={() => this.scrollToNext()}
                  disabled={this.isScrolling}
                  aria-label="Next cards"
                >
                  <svg
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path d="M8.59 16.59L10 18l6-6-6-6-1.41 1.41L13.17 12z" fill="currentColor" />
                  </svg>
                </button>
              )}
              <div class="scrollable-cards" ref={el => (this.carouselRef = el as HTMLDivElement)}>
                {this.cards?.map(card => (
                  <div class={`${this.isMobile ? 'mobile-card' : 'tablet-card'}`}>
                    <div class="image-container">
                      {card.mediaType === 'video' ? (
                        <div class="video-container">
                          <video
                            class="video"
                            autoplay
                            muted
                            loop
                            playsinline
                            poster={card.thumbnailImageUrl || card.imageUrl}
                            src={card.videoUrl}
                          ></video>
                        </div>
                      ) : (
                        <div
                          class="image cardReveal"
                          style={{
                            backgroundImage: card.imageUrl ? `url(${card.imageUrl})` : 'none',
                          }}
                        ></div>
                      )}
                    </div>
                    <div class="copy-container">
                      <div class="title-container">
                        {card['card-link'] ? (
                          <a
                            href={card['card-link']}
                            class="analytics-link title-link"
                            aria-label="Read more about this card"
                          >
                            <h4 class="title blockReveal">{card.title}</h4>
                            <svg
                              class="title-arrow-icon"
                              width="16"
                              height="16"
                              viewBox="0 0 16 16"
                              xmlns="http://www.w3.org/2000/svg"
                            >
                              <path
                                d="M8 0L6.59 1.41L12.17 7H0V9H12.17L6.59 14.59L8 16L16 8L8 0Z"
                                fill="currentColor"
                              />
                            </svg>
                          </a>
                        ) : (
                          <h4 class="title blockReveal">{card.title}</h4>
                        )}
                      </div>
                      <p class="description blockReveal">{card.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : this.isTablet ? (
            <div class="tablet-carousel">
              {this.canScrollLeft && (
                <button
                  class="nav-button prev-button"
                  onClick={() => this.scrollToPrev()}
                  aria-label="Previous cards"
                >
                  <svg
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path d="M15.41 7.41L14 6l-6 6 6 6 1.41-1.41L10.83 12z" fill="currentColor" />
                  </svg>
                </button>
              )}
              {this.canScrollRight && (
                <button
                  class="nav-button next-button"
                  onClick={() => this.scrollToNext()}
                  aria-label="Next cards"
                >
                  <svg
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path d="M8.59 16.59L10 18l6-6-6-6-1.41 1.41L13.17 12z" fill="currentColor" />
                  </svg>
                </button>
              )}
              <div class="scrollable-cards" ref={el => (this.carouselRef = el as HTMLDivElement)}>
                {this.cards?.map(card => (
                  <div class="tablet-card">
                    <div class="image-container">
                      {card.mediaType === 'video' ? (
                        <div class="video-container">
                          <video
                            class="video"
                            autoplay
                            muted
                            loop
                            playsinline
                            poster={card.thumbnailImageUrl || card.imageUrl}
                            src={card.videoUrl}
                          ></video>
                        </div>
                      ) : (
                        <div
                          class="image cardReveal"
                          style={{
                            backgroundImage: card.imageUrl ? `url(${card.imageUrl})` : 'none',
                          }}
                        ></div>
                      )}
                    </div>
                    <div class="copy-container">
                      <div class="title-container">
                        {card['card-link'] ? (
                          <a
                            href={card['card-link']}
                            class="analytics-link title-link"
                            aria-label="Read more about this card"
                          >
                            <h4 class="title blockReveal">{card.title}</h4>
                            <svg
                              class="title-arrow-icon"
                              width="16"
                              height="16"
                              viewBox="0 0 16 16"
                              xmlns="http://www.w3.org/2000/svg"
                            >
                              <path
                                d="M8 0L6.59 1.41L12.17 7H0V9H12.17L6.59 14.59L8 16L16 8L8 0Z"
                                fill="currentColor"
                              />
                            </svg>
                          </a>
                        ) : (
                          <h4 class="title blockReveal">{card.title}</h4>
                        )}
                      </div>
                      <p class="description blockReveal">{card.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : this.isTablet ? (
            <div class="tablet-carousel">
              {this.canScrollLeft && (
                <button
                  class="nav-button prev-button"
                  onClick={() => this.scrollToPrev()}
                  aria-label="Previous cards"
                >
                  <svg
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path d="M15.41 7.41L14 6l-6 6 6 6 1.41-1.41L10.83 12z" fill="currentColor" />
                  </svg>
                </button>
              )}
              {this.canScrollRight && (
                <button
                  class="nav-button next-button"
                  onClick={() => this.scrollToNext()}
                  aria-label="Next cards"
                >
                  <svg
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path d="M8.59 16.59L10 18l6-6-6-6-1.41 1.41L13.17 12z" fill="currentColor" />
                  </svg>
                </button>
              )}
              <div class="scrollable-cards" ref={el => (this.carouselRef = el as HTMLDivElement)}>
                {this.cards?.map(card => (
                  <div class="mobile-card">
                    <div class="image-container">
                      {card.mediaType === 'video' ? (
                        <div class="video-container">
                          <video
                            class="video"
                            autoplay
                            muted
                            loop
                            playsinline
                            poster={card.thumbnailImageUrl || card.imageUrl}
                            src={card.videoUrl}
                          ></video>
                        </div>
                      ) : (
                        <div
                          class="image cardReveal"
                          style={{
                            backgroundImage: card.imageUrl ? `url(${card.imageUrl})` : 'none',
                          }}
                        ></div>
                      )}
                    </div>
                    <div class="copy-container">
                      <div class="title-container">
                        {card['card-link'] ? (
                          <a
                            href={card['card-link']}
                            class="analytics-link title-link"
                            aria-label="Read more about this card"
                          >
                            <h4 class="title blockReveal">{card.title}</h4>
                            <svg
                              class="title-arrow-icon"
                              width="16"
                              height="16"
                              viewBox="0 0 16 16"
                              xmlns="http://www.w3.org/2000/svg"
                            >
                              <path
                                d="M8 0L6.59 1.41L12.17 7H0V9H12.17L6.59 14.59L8 16L16 8L8 0Z"
                                fill="currentColor"
                              />
                            </svg>
                          </a>
                        ) : (
                          <h4 class="title blockReveal">{card.title}</h4>
                        )}
                      </div>
                      <p class="description blockReveal">{card.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div
              class={`editorial-grid editorial-card-grid ${this.cards?.length > 4 ? 'cards-more-than-4' : 'cards-4-or-less'}`}
            >
              {this.cards?.map(card => (
                <div class="card-wrapper">
                  {card.mediaType === 'video' ? (
                    // Keep video cards as they are for now since vsr-card doesn't handle video
                    <div class="card">
                      <div class="container">
                        <div class="video-container">
                          <video
                            class="video"
                            autoplay
                            muted
                            loop
                            playsinline
                            poster={card.thumbnailImageUrl || card.imageUrl}
                            src={card.videoUrl}
                          ></video>
                        </div>
                      </div>
                      <div class="copy-container">
                        <div class="title-container">
                          {card['card-link'] ? (
                            <a
                              href={card['card-link']}
                              class="analytics-link title-link"
                              aria-label="Read more about this card"
                            >
                              <h4 class="title blockReveal">{card.title}</h4>
                              <svg
                                class="title-arrow-icon"
                                width="16"
                                height="16"
                                viewBox="0 0 16 16"
                                xmlns="http://www.w3.org/2000/svg"
                              >
                                <path
                                  d="M8 0L6.59 1.41L12.17 7H0V9H12.17L6.59 14.59L8 16L16 8L8 0Z"
                                  fill="currentColor"
                                />
                              </svg>
                            </a>
                          ) : (
                            <h4 class="title blockReveal">{card.title}</h4>
                          )}
                        </div>
                        <p class="description blockReveal">{card.description}</p>
                      </div>
                    </div>
                  ) : (
                    // Use vsr-card for image cards with dynamic aspect ratio support
                    <vsr-card
                      card-variant="default"
                      image-variant="variant1"
                      image-url={card.imageUrl}
                      image-alt-text={card.imageAltText || card.title}
                      image-aspect-ratio={card.imageAspectRatio}
                      title={card.title}
                      description={card.description}
                      cardLink={card['card-link']}
                      cardLinkTarget={card['card-link-target']}
                      theme="light"
                      animation="zoom"
                    ></vsr-card>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  }
}
